/**
 * SHER-383 — persistence mode contract for treatment smart fields.
 *
 * Inpatient ward prescriptions save each medication row immediately.
 * Outpatient ordonnances (and regular/chronic treatment lists) accumulate rows
 * in the form and persist once on submit. Routing outpatient creates through
 * onSaveToDatabase caused one submission per medication (Pedro, 2026-06-21).
 */

export type TreatmentPersistAction = 'immediate-save' | 'immediate-update' | 'accumulate';

/** Field types that POST/PUT each row as it is added or edited. */
export const IMMEDIATE_ROW_PERSISTENCE_FIELD_TYPES = ['treatment_inpatient'] as const;

export function usesImmediateRowPersistence(fieldType: string): boolean {
    return fieldType === 'treatment_inpatient';
}

export function resolveTreatmentPersistAction({
    fieldType,
    isEditing,
    hasOnSaveToDatabase,
    hasOnUpdateDatabase,
}: {
    fieldType: string;
    isEditing: boolean;
    hasOnSaveToDatabase: boolean;
    hasOnUpdateDatabase: boolean;
}): TreatmentPersistAction {
    if (!usesImmediateRowPersistence(fieldType)) {
        return 'accumulate';
    }

    if (isEditing && hasOnUpdateDatabase) {
        return 'immediate-update';
    }

    if (!isEditing && hasOnSaveToDatabase) {
        return 'immediate-save';
    }

    return 'accumulate';
}

/**
 * Outpatient ordonnance rows accumulate locally (SHER-383) and have no per-row
 * submission_id until the form is saved. Deleting them must not call DELETE
 * /submission/undefined (HTTP 500 on CHOM prod).
 */
export function shouldDeleteTreatmentRowViaApi(
    fieldType: string,
    submissionId: unknown,
): boolean {
    return usesImmediateRowPersistence(fieldType) && submissionId != null && submissionId !== '';
}
